import { FC, useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import {
  EngagementFieldsFragment,
  EditEngagementLocationDocument,
  TimelineDocument,
} from '@flashpack/graphql';
import { Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { FreeTextFormField } from 'design-system';
import { useMutation } from '@apollo/client';
import { useToast } from '@src/shared/toast/useToast';
import { useSafeMutation } from '@src/shared/useSafeMutation';
import { Decimal } from 'decimal.js';
import { MapLocationField } from '@src/shared/map/MapLocationField';
import { useContext } from 'react';
import { TimelineContext } from '@src/shared/timeline/TimelineContext';

interface PropTypes {
  engagement: EngagementFieldsFragment;
}

type Coordinates = {
  latitude: string;
  longitude: string;
};

interface LocationFormState {
  startName: string;
  endName?: string;
  startMapLocation: Coordinates;
  endMapLocation?: Coordinates;
}

export const EngagementLocationTab: FC<PropTypes> = ({ engagement }) => {
  const { success: successToast, error: errorToast } = useToast();
  const { safeMutation } = useSafeMutation();
  const { timelineId } = useContext(TimelineContext);
  const [editLocation] = useMutation(EditEngagementLocationDocument);

  const isTransfer = !!engagement.transfer;

  const initialValues = useMemo(() => {
    return {
      startName: engagement.startMapLocation?.name ?? '',
      ...(isTransfer && {
        endName: engagement.endMapLocation?.name ?? '',
        endLatitude: engagement.endMapLocation?.latitude?.toString() ?? '',
        endLongitude: engagement.endMapLocation?.longitude?.toString() ?? '',
      }),
      startLatitude: engagement.startMapLocation?.latitude?.toString() ?? '',
      startLongitude: engagement.startMapLocation?.longitude?.toString() ?? '',
    };
  }, [engagement, isTransfer]);

  const handleSubmit = useCallback(
    async (formData: LocationFormState) => {
      if (!formData.startMapLocation.latitude || !formData.startMapLocation.longitude) {
        errorToast('Please select a location on the map first');
        return;
      }

      await safeMutation(
        editLocation({
          variables: {
            input: {
              id: engagement.id,
              startMapLocation: {
                name: formData.startName,
                latitude: new Decimal(formData.startMapLocation.latitude),
                longitude: new Decimal(formData.startMapLocation.longitude),
              },
              ...(isTransfer && {
                endMapLocation: {
                  name: formData.endName || formData.startName,
                  latitude: new Decimal(formData.endMapLocation!.latitude),
                  longitude: new Decimal(formData.endMapLocation!.longitude),
                },
              }),
            },
          },
          refetchQueries: [
            {
              query: TimelineDocument,
              variables: { id: timelineId },
            },
          ],
        }),
        {
          onSuccess: () => {
            successToast('Location successfully updated');
          },
          onError: () => {
            errorToast(
              'There was an issue while updating the location - try again later',
            );
          },
        },
      );
    },
    [
      engagement.id,
      editLocation,
      safeMutation,
      successToast,
      errorToast,
      isTransfer,
      timelineId,
    ],
  );

  const validate = (values: LocationFormState) => {
    const errors: {
      startName?: string;
      endName?: string;
      startMapLocation?: {
        name?: string;
        latitude?: string;
        longitude?: string;
      };
    } = {};

    // Validate start location
    if (!values.startName) {
      errors.startName = 'Name is required';
    }

    // Validate end location name for transfers
    if (isTransfer && !values.endName) {
      errors.endName = 'Name is required';
    }

    return errors;
  };

  return (
    <Stack spacing={3} pt={2}>
      <Typography variant="Body M" color="text.secondary">
        Select the location on the map that will be displayed on the itinerary map on the
        website.
      </Typography>

      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validate}
        key={`location-form-${engagement.id}`}
        render={({ handleSubmit, pristine, submitting, valid, values }) => {
          return (
            <form onSubmit={(form) => void handleSubmit(form)}>
              <Stack spacing={3}>
                <Stack spacing={1}>
                  <Typography sx={{ color: 'principal.grey70' }}>
                    Location Name
                  </Typography>
                  <FreeTextFormField
                    name="startName"
                    placeholder="Enter location name"
                    data-testid="start-location-name-input"
                    value={values.startName}
                  />
                </Stack>

                <MapLocationField
                  name="startMapLocation"
                  label="Map Location"
                  required
                  key={`map-location-${engagement.id}`}
                  initialLocation={{
                    latitude: engagement.startMapLocation?.latitude?.toString() ?? '',
                    longitude: engagement.startMapLocation?.longitude?.toString() ?? '',
                  }}
                />

                {isTransfer && (
                  <>
                    <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
                      End Location Name
                    </Typography>
                    <Stack spacing={1}>
                      <Typography sx={{ color: 'principal.grey70' }}>
                        Location Name (displayed for end point)
                      </Typography>
                      <FreeTextFormField
                        name="endName"
                        placeholder="Enter location name"
                        required
                        data-testid="end-location-name-input"
                      />
                    </Stack>
                  </>
                )}

                <Stack direction="row" justifyContent="flex-end">
                  <LoadingButton
                    loading={submitting}
                    disabled={pristine || submitting || !valid}
                    type="submit"
                    variant="contained"
                    color="success"
                    data-testid="location-save-button"
                  >
                    Save Changes
                  </LoadingButton>
                </Stack>
              </Stack>
            </form>
          );
        }}
      />
    </Stack>
  );
};
